import {
  CoverageNeed,
  NeedName,
  ProductCoverageName,
  ProductName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  getPaymentRestriction,
  isNotAllowed,
} from "Store/akaAdvice/interfaces/getters";
import {
  AkaAdviceCheckboxes,
  AkaAdvicePriceFormValues,
} from "../../interfaces";

import { StyledAlert, StyledRow } from "App/global-style";
import CollapsibleContainer from "Components/CollapsibleContainer";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { DetailsCardWrapper } from "Components/InsuranceDetailsCard/styled";
import { VALUE_TYPE } from "Components/NumberValue";
import { AdvicePlotDeath } from "Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/AdvicePlots/Death/AdvicePlotDeath";
import { AdviceTextsDeathCommonChildren } from "Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/AdviceTexts/AdviceTextsDeathCommonChildren";
import { AlreadyInsured } from "Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/AlreadyInsured";
import { ProductCoverages } from "Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/ProductCoverages";
import {
  getPurchaseFromAdvisorMessage,
  replaceUserSupportNumberInText,
} from "Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/utils";
import { useAdviceDetailsFormValues } from "Features/CheckPrice/Products/Advice/ProductPrice/hooks/stateSelectors";
import { mapInsuranceNameToAdviceCoverageName } from "Features/CheckPrice/Products/Advice/ProductPrice/utils";
import { Description } from "Features/CheckPrice/Products/Advice/adviceStyled";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { useAkaAdviceBaseAmountSelector } from "Store/akaAdvice/akaAdviceSelectors";
import { isAkaGroupLifeDeathSelected } from "Store/akaAdvice/mappers/mapToCartProducts/mapToCartProducts";
import { akaAdvicePriceCalculationRequestStart } from "Store/akaAdvice/slice";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import {
  useIsAkademikerneBranding,
  usePartnerId,
} from "Store/partners/partnerHooks";
import { TextLanguage } from "Store/texts/interfaces";
import { FormikProps } from "formik";
import React from "react";
import { useDispatch } from "react-redux";
import { checkShouldPurchaseFromAdvisor } from "../../../utils";
import { AdviceCardComment } from "../../AdviceCardComment";
import { AdviceCardHeader } from "../../AdviceCardHeader";
import { InsurancePrice } from "../../AdvicePrice/InsurancePrice";
import { AdvisorUwInfo } from "../../AdvisorUwInfo";
import { CloseToMaxAgeWarning } from "../../CloseToMaxAgeWarning";
import { usePriceStatus } from "../../hooks/usePriceStatus";
import { ContentWrapper } from "../../styled";
import { getIndividualAdvicePlotRange } from "../../utils";
import { IndividualDeathForm } from "../Forms/IndividualDeathForm";
import { DeathAdviceText } from "../Texts/DeathAdviceText";
import { DEATH_END_AGE, DEATH_MAX_AGE } from "../deathAdviceCardConstants";

export interface Props {
  formProps: FormikProps<AkaAdvicePriceFormValues>;
  need: CoverageNeed;
  adviceCard: any;
  lang: TextLanguage;
}

export const DeathAdviceCard = ({
  formProps,
  need,
  adviceCard,
  lang,
}: Props): React.ReactElement | null => {
  const isAkaBranding = useIsAkademikerneBranding();
  const partnerId = usePartnerId();
  const dispatch = useDispatch();
  const adviceDetails = useAdviceDetailsFormValues();
  const isAdvisor = useIsAdvisorSelector();
  const baseAmount = useAkaAdviceBaseAmountSelector();
  const selected = formProps.values.deathCheckbox;
  const currentSliderValue = formProps.values.deathInsuredSumSlider;
  const currentGValue = isAkaGroupLifeDeathSelected(formProps.values)
    ? Number(formProps.values.akaGroupLifeDeathGLevel ?? 0) * baseAmount
    : 0;
  const {
    priceIsChanging,
    setPriceIsChanging,
    priceIsLoading,
    setPriceIsLoading,
  } = usePriceStatus();
  const insuranceType: string = adviceCard.insuranceType[lang];

  const recalculatePrice = (
    formValues: AkaAdvicePriceFormValues,
    specificProduct: ProductName,
  ): void => {
    setPriceIsChanging(false);
    setPriceIsLoading(true);
    dispatch(
      akaAdvicePriceCalculationRequestStart({
        isCollectiveAdvice: isAkaBranding,
        priceFormValues: formValues,
        specificProduct,
      }),
    );
  };

  if (!adviceDetails || isNotAllowed(need, ProductCoverageName.DEATH)) {
    return null;
  }

  const paymentRestriction = getPaymentRestriction(
    need,
    ProductCoverageName.DEATH,
  );
  const shouldPurchaseFromAdvisor = checkShouldPurchaseFromAdvisor(
    isAdvisor,
    need,
    ProductCoverageName.DEATH,
  );

  const plotRange = getIndividualAdvicePlotRange(
    isAdvisor,
    adviceDetails.debt,
    paymentRestriction,
  );

  return (
    <div className="AdviceCard_DEATH">
      <DetailsCardWrapper adviceDetailsCard selected={selected}>
        <StyledRow>
          <AdviceCardHeader
            insuranceId={AkaAdviceCheckboxes.DEATH_CHECKBOX}
            insuranceType={insuranceType}
          />
          <InsurancePrice
            needName={NeedName.LIFE}
            productCoverages={[ProductCoverageName.DEATH]}
            priceIsChanging={priceIsChanging}
            priceIsLoading={priceIsLoading}
          />
        </StyledRow>
        <ContentWrapper className="AdviceCardContent">
          <div className="AdviceHeaderText">
            <Description fw={600} size="16px">
              Livsforsikring kan hjelpe de etterlatte som må klare seg uten din
              inntekt og utbetales til dine etterlatte uansett dødsårsak.
            </Description>
          </div>
          <Description fw={700} size="16px">
            Tilpass engangsutbetaling til dine etterlatte
          </Description>
          <DeathAdviceText
            need={need}
            adviceDetails={adviceDetails}
            adviceTexts={{
              ...adviceCard.adviceTexts,
              adviceUnavailableMessages: adviceCard.adviceUnavailableMessages,
            }}
            lang={lang}
          />

          <CollapsibleContainer isOpen={selected}>
            <AdvicePlotDeath
              debt={adviceDetails.debt}
              payout={currentSliderValue ?? 0}
              valueType={VALUE_TYPE.NOK}
              advicePlot={adviceCard.advicePlot}
              lang={lang}
              range={plotRange}
            />
            <IndividualDeathForm
              formProps={formProps}
              need={need}
              recalculatePrice={recalculatePrice}
              setPriceIsChanging={setPriceIsChanging}
              adviceCard={adviceCard}
              lang={lang}
            />
            <CloseToMaxAgeWarning
              need={need}
              coverageName={ProductCoverageName.DEATH}
              warningTexts={adviceCard.warnings}
              lang={lang}
              warningVariables={{
                maxAge: DEATH_MAX_AGE,
                endAge: DEATH_END_AGE,
              }}
            />

            {isAdvisor && (
              <AdvisorUwInfo
                insuranceName={INSURANCE_NAME_VALUE.DEATH}
                sliderValue={currentSliderValue ?? 0}
                paymentRestriction={paymentRestriction}
                uwInfoTexts={adviceCard.advisorTexts.uwInfoTexts}
                lang={lang}
                gValue={currentGValue}
              />
            )}
            {!isAdvisor &&
              !shouldPurchaseFromAdvisor &&
              currentSliderValue === paymentRestriction.maxPayouts.CUSTOMER && (
                <StyledAlert
                  className="PurchaseFromAdvisorText"
                  title={replaceUserSupportNumberInText(
                    partnerId,
                    adviceCard.adviceUnavailableMessages.purchaseFromAdvisor
                      .userSupportNumber,
                    adviceCard.adviceUnavailableMessages.purchaseFromAdvisor
                      .maxPayoutCustomer[lang],
                    lang,
                  )}
                />
              )}
            {shouldPurchaseFromAdvisor && (
              <StyledAlert
                className="PurchaseFromAdvisorText"
                title={getPurchaseFromAdvisorMessage(
                  isAdvisor,
                  partnerId,
                  need,
                  ProductCoverageName.DEATH,
                  adviceCard.adviceUnavailableMessages.purchaseFromAdvisor,
                  lang,
                )}
              />
            )}

            <AdviceTextsDeathCommonChildren
              familySituation={adviceDetails.familySituation}
            />
            <AlreadyInsured insuranceType={insuranceType} />
            <ProductCoverages
              selected={selected}
              readableInsuranceType={insuranceType}
              backendInsuranceName={ProductBackendName.DEATH}
              coverageGroup={mapInsuranceNameToAdviceCoverageName(
                INSURANCE_NAME_VALUE.DEATH,
              )}
            />
          </CollapsibleContainer>
          <AdviceCardComment
            commentId="deathAdviceComment"
            formProps={formProps}
            label="Notater om anbefaling og kundens valg av livsforsikring"
          />
        </ContentWrapper>
      </DetailsCardWrapper>
    </div>
  );
};
